// Task 2.5 (org/team/individual forecast sharing) — the ONE shared source
// for "is this row read-only" and "how should its owner attribution read",
// consumed identically by WatchlistRow and RecentRow (forecastLog.jsx)
// so a fourth row variant added later inherits the same gate rather than
// needing its own copy of the check (review: "gate at the level where new
// things arrive" — a single source all variants consume beats three
// independent re-derivations of the same boolean).
//
// No React imports — pure predicate/formatter functions (CLAUDE.md's
// "no React imports in lib/" rule).
import { isSayerManaged } from "../featured/observerCopy.js";

/**
 * True iff `f` is a shared-with-me row (never editable by the viewer).
 * `f.isOwn` defaults to `true` for every pre-existing owner-leg row (see
 * model.ts's `Run.isOwn` doc) — only a row hydrated from
 * `GET /api/forecasts/shared` ever carries `isOwn: false`. Strict `=== false`
 * (not `!f.isOwn`) so a row with no `isOwn` field at all (a legacy/partial
 * object some older code path might still construct) is treated as OWNED,
 * never as read-only by accident — the fail-safe direction for a boolean
 * that gates hiding controls, not granting them.
 *
 * @param {{isOwn?: boolean}|null|undefined} f
 * @returns {boolean}
 */
export const isReadOnlyRow = (f) => f?.isOwn === false;

/**
 * A short "Shared by <name>" label for a shared row's owner attribution.
 * Falls back to the owner's email when no display name is set, and to a
 * generic label when neither is available (a lookup miss server-side —
 * `listSharedForecasts.js`'s own `owner: {name: null, email: null}` case).
 *
 * @param {{owner?: {name?: string|null, email?: string|null}}|null|undefined} f
 * @returns {string}
 */
export const ownerAttributionLabel = (f) => {
  const owner = f?.owner;
  const who = owner?.name || owner?.email;
  return who ? `Shared by ${who}` : "Shared with you";
};

/**
 * True iff the viewer may EDIT this forecast — its cadence, its forecaster, or
 * its branches. One predicate for all three, matching the server's single
 * `canEditSharedForecast`.
 *
 * Deliberately NOT `!isReadOnlyRow(f)`. Those two questions came apart with the
 * collaborative design: the cadence is a property of the QUESTION — one timeline
 * every holder experiences — so an in-org collaborator sets it on a row that is
 * read-only in every other respect (they still cannot delete it, reorder it, or
 * see the owner's alert prefs). An out-of-org collaborator sees the same cadence
 * and cannot set it; they are offered their own copy instead.
 *
 * The answer is the server's (`canEdit` on the shared wire, from the same
 * predicate `PUT /:id/cadence` enforces). This function only supplies the
 * fail-safe default for a row that predates the field — TRUE, because every such
 * row is an owner-leg row, and an owner has always been able to set their own
 * cadence. The route re-checks on every write, so a wrong guess here costs a
 * refused request, never an unauthorized one.
 *
 * @param {{canEdit?: boolean}|null|undefined} f
 * @returns {boolean}
 */
export const canEdit = (f) => f?.canEdit !== false;

/**
 * May the viewer change this forecast's update schedule and depth? Not on a
 * copy of one of Sayer's own forecasts, which keeps Sayer's schedule (lib/featured/observerCopy.js);
 * its scenarios, alerts, watchlist, sharing and delete stay as for any forecast, so this is a
 * separate guard rather than a change to `canEdit`.
 *
 * @param {{canEdit?: boolean, serverContext?: object}|null|undefined} f
 * @returns {boolean}
 */
export const canChangeUpdates = (f) => canEdit(f) && !isSayerManaged(f);

/**
 * May the viewer run Update now on this forecast? The same people who may set its schedule
 * (`canEdit`): a fresh run changes the probability and report every holder sees, and the save
 * lands on the owner's row. A copy of one of Sayer's forecasts is updated by first making it
 * its holder's own, which only that holder can do, so a shared one is refused.
 *
 * @param {{isOwn?: boolean, canEdit?: boolean, serverContext?: object}|null|undefined} f
 * @returns {boolean}
 */
export const canUpdateNow = (f) => canEdit(f) && !(isReadOnlyRow(f) && isSayerManaged(f));
